import React, { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';

import { SortOrder, WiderTable } from '@/components';
import { useQueryParams } from '@/hooks';

import { getColumns } from './columns';
import { useFollowList, FollowListFrom } from './hook';
import style from './index.module.scss';
import { openNewWindow } from '@/utils';
import { getDefaultOrder } from './utils';

type Props = {
  id: string;
  className?: string;
  from?: FollowListFrom;
  isFollowing?: boolean;
  chainId?: string;
};

export const FollowList = (props: Props) => {
  const { id, className, from = 'user', isFollowing, chainId } = props;
  const limit = 100;
  const [start, setStart] = useQueryParams('start', 0);
  const [orderBy, setOrderBy] = useState(getDefaultOrder(from, isFollowing));

  const { data, refetch, isError, isLoading } = useFollowList({
    id,
    start,
    limit,
    from,
    isFollowing,
    orderBy: orderBy,
    chainId,
  });

  const pagination = useMemo(
    () => ({
      current: Math.floor(start / limit) + 1,
      defaultPageSize: limit,
      onChange: (page: number) => {
        setStart(limit * (page - 1));
      },
      total: data?.total_count,
      showQuickJumper: true,
      showSizeChanger: false,
    }),
    [start, limit, data?.total_count, setStart],
  );

  const onRowClick = useCallback(row => {
    openNewWindow(`/profile/${row.id}`, '_blank');
  }, []);

  const onSort = useCallback(
    ([[key, sort]]: Array<[string, SortOrder]>) => {
      if (key) {
        // official list 排序不用+号
        if (from === 'official') {
          setOrderBy(`${sort === 'desc' ? '-' : ''}${key}`);
          return;
        }
        setOrderBy(`${sort === 'desc' ? '-' : '+'}${key}`);
      }
    },
    [setOrderBy, from],
  );
  const columns = useMemo(
    () => getColumns({ refetch, isFollowing, from }),
    [isFollowing, refetch, from],
  ) as any;

  return (
    <WiderTable
      onRowClick={onRowClick}
      className={clsx(style.followList, className)}
      columns={columns}
      data={data?.followers || data?.following_list || []}
      reload={refetch}
      isError={isError}
      loading={isLoading}
      pagination={pagination}
      orderBy={orderBy}
      onSort={onSort}
    />
  );
};

export { invalidFollowList } from './hook';
